import React, { useState, useEffect, useRef, useCallback } from "react";
import { styled } from "../styled/theme";

// Minimum scale prevents the board from becoming unusably small
const MIN_SCALE = 0.65;

const OuterWrapper = styled("div", {
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
});

const InnerWrapper = styled("div", {
  transformOrigin: "center",
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

function getViewportSize() {
  if (window.visualViewport) {
    return {
      width: window.visualViewport.width,
      height: window.visualViewport.height,
    };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

interface ScaleWrapperProps {
  enabled: boolean;
}

export const ScaleWrapper: React.FC<ScaleWrapperProps> = ({
  children,
  enabled,
}) => {
  const [scale, setScale] = useState(1);
  // This ref measures the ACTUAL content at scale=1 — it sits outside the scaled wrapper
  const measureRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    if (!enabled || !measureRef.current) return;
    const { width: vw, height: vh } = getViewportSize();
    const { width: cw, height: ch } = measureRef.current.getBoundingClientRect();
    if (cw === 0 || ch === 0) return;
    const scaleX = vw / cw;
    const scaleY = vh / ch;
    // Tight padding: only 2px on each side (4px total) to maximize screen usage
    setScale(Math.max(Math.min(scaleX, scaleY) * 0.995, MIN_SCALE));
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setScale(1);
      return;
    }

    // Recalculate after a short delay so React has finished laying out
    const timer = setTimeout(recalculate, 0);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", recalculate);
    }
    window.addEventListener("resize", recalculate);
    window.addEventListener("orientationchange", recalculate);

    return () => {
      clearTimeout(timer);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", recalculate);
      }
      window.removeEventListener("resize", recalculate);
      window.removeEventListener("orientationchange", recalculate);
    };
  }, [enabled, recalculate]);

  // When disabled, render children directly without any wrapper constraints
  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <OuterWrapper>
      <InnerWrapper css={{ transform: `scale(${scale})` }}>
        {children}
      </InnerWrapper>
      {/*
        Hidden measurement element: sits outside the scaled wrapper so it
        always reports the true unscaled dimensions. Positioned off-screen
        but still in-document so layout is computed correctly.
      */}
      <div
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </OuterWrapper>
  );
};
